import React from "react";
import { useTranslation } from "react-i18next";

import { Box, InputAdornment, Stack, TextField } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { getEndOfPeriod } from "utils/debt";
import toIsoMidday from "utils/to-iso-midday";

import { FloatTypeSelect } from "../../form-fields/FloatTypeSelect";
import { RateFixingTypeSelect } from "../../form-fields/RateFixingTypeSelect";
import { ReferenceIndexSelect } from "../../form-fields/ReferenceIndexSelect";
import { FormValues } from "../../schemas";
import { DAY_COUNT_CONVENTION_OPTIONS, getFrequencyOptions } from "../constants";
import { OptionCard } from "../OptionCard";
import { QuestionSlot } from "../QuestionSlot";
import { IndexField } from "../types";

interface Step4Props {
  formik: FormikProps<FormValues>;
  dateFormat: string;
  isSimpleLoan: boolean;
  hasRegularInterest: boolean;
  hasPIK: boolean;
  revealedFields: Set<string>;
  reveal: (name: string) => void;
  fetchIndexValue: (index: string, field: IndexField) => Promise<void>;
}

export const Step4InterestTerms: React.FC<Step4Props> = ({
  formik,
  dateFormat,
  isSimpleLoan,
  hasRegularInterest,
  hasPIK,
  revealedFields,
  reveal,
  fetchIndexValue
}) => {
  const { t } = useTranslation("debt");
  const { values, setFieldValue } = formik;

  return (
    <Box>
      <QuestionSlot visible={hasRegularInterest} label={t("wizard.interestTerms.baseRateQuestion")}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            type="number"
            label={t("wizard.interestTerms.baseRateLabel")}
            value={values.base_rate ?? ""}
            onChange={(e) => {
              setFieldValue("base_rate", e.target.value === "" ? null : parseFloat(e.target.value));
              reveal("base_rate");
            }}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
          />
          <FloatTypeSelect
            name="float_type"
            formik={formik}
            label={t("wizard.interestTerms.rateStructureLabel")}
            onChange={(e) => {
              formik.handleChange(e);
              if (e.target.value === "fixed") {
                setFieldValue("interest_reference_index", null);
                setFieldValue("interest_index_value", null);
              }
              if (e.target.value !== "linked") {
                setFieldValue("interest_link_percent", 0);
              }
              if (e.target.value !== "spread") {
                setFieldValue("interest_rate_fixing", null);
              }
              reveal("float_type");
            }}
          />
        </Stack>
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && values.float_type !== "fixed"}
        label={t("wizard.interestTerms.indexQuestion")}
      >
        <ReferenceIndexSelect
          name="interest_reference_index"
          formik={formik}
          label={t("wizard.interestTerms.indexLabel")}
          onIndexChange={(idx) => fetchIndexValue(idx, "interest_index_value")}
        />
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && values.float_type === "spread"}
        label={t("wizard.interestTerms.rateFixingQuestion")}
      >
        <RateFixingTypeSelect
          name="interest_rate_fixing"
          formik={formik}
          label={t("wizard.interestTerms.rateFixingLabel")}
        />
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && values.float_type === "linked"}
        label={t("wizard.interestTerms.linkPercentQuestion")}
      >
        <TextField
          fullWidth
          type="number"
          label={t("wizard.common.indexLinkPercentage")}
          value={values.interest_link_percent || ""}
          onChange={(e) => setFieldValue("interest_link_percent", parseFloat(e.target.value) || 0)}
          slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
        />
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && values.float_type === "spread" && values.interest_rate_fixing === "in_advance"}
        label={t("wizard.interestTerms.loanResetFrequencyQuestion")}
      >
        <Stack spacing={3}>
          <Select
            name="loan_reset_frequency"
            label={t("wizard.interestTerms.loanResetFrequencyLabel")}
            variant="outlined"
            fullWidth
            value={values.loan_reset_frequency ?? ""}
            items={[{ key: "never", value: "", text: t("wizard.common.never") }, ...getFrequencyOptions(t)]}
            onChange={(e) => {
              const freq = e.target.value || null;
              setFieldValue("loan_reset_frequency", freq);
              if (isSimpleLoan && freq && values.start_date)
                setFieldValue("loan_reset_date", getEndOfPeriod(dayjs(values.start_date), freq));
            }}
          />
          <TextField
            fullWidth
            type="number"
            name="loan_reset_date_offset"
            label={t("wizard.common.resetDateOffset")}
            value={values.loan_reset_date_offset ?? ""}
            onChange={(e) =>
              setFieldValue("loan_reset_date_offset", e.target.value === "" ? null : parseInt(e.target.value, 10))
            }
            slotProps={{ input: { inputProps: { step: 1 } } }}
          />
        </Stack>
      </QuestionSlot>

      <QuestionSlot
        visible={
          isSimpleLoan &&
          hasRegularInterest &&
          values.float_type === "spread" &&
          values.interest_rate_fixing === "in_advance"
        }
        label={t("wizard.interestTerms.loanResetDateQuestion")}
      >
        <Stack spacing={3}>
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("wizard.interestTerms.loanResetDateLabel")}
            value={values.loan_reset_date ? dayjs(values.loan_reset_date) : null}
            onChange={(v) => setFieldValue("loan_reset_date", v?.isValid() ? toIsoMidday(v) : null)}
            sx={{ width: "100%" }}
            slotProps={{ textField: { helperText: t("wizard.common.autoCalculated") } }}
          />
        </Stack>
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && revealedFields.has("base_rate")}
        label={t("wizard.interestTerms.frequencyQuestion")}
      >
        <Select
          name="interest_frequency"
          label={t("wizard.interestTerms.frequencyLabel")}
          variant="outlined"
          fullWidth
          value={values.interest_frequency ?? ""}
          items={getFrequencyOptions(t)}
          onChange={(e) => {
            setFieldValue("interest_frequency", e.target.value || null);
            if (isSimpleLoan && values.start_date && e.target.value)
              setFieldValue("interest_first_payment_date", getEndOfPeriod(dayjs(values.start_date), e.target.value));
            reveal("interest_frequency");
          }}
        />
        {isSimpleLoan && values.interest_frequency && (
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("wizard.interestTerms.firstPaymentLabel")}
            value={values.interest_first_payment_date ? dayjs(values.interest_first_payment_date) : null}
            onChange={(v) => setFieldValue("interest_first_payment_date", v?.isValid() ? toIsoMidday(v) : null)}
            sx={{ width: "100%", mt: 3 }}
            slotProps={{ textField: { helperText: t("wizard.common.autoCalculated") } }}
          />
        )}
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && revealedFields.has("interest_frequency")}
        label={t("wizard.interestTerms.dayCountQuestion")}
      >
        <Select
          name="day_count_convention"
          label={t("wizard.common.dayCountConvention")}
          variant="outlined"
          fullWidth
          value={values.day_count_convention}
          items={DAY_COUNT_CONVENTION_OPTIONS}
          onChange={formik.handleChange}
        />
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && revealedFields.has("interest_frequency")}
        label={t("wizard.interestTerms.floorCeilingQuestion")}
      >
        <Stack direction="row" spacing={3}>
          <TextField
            fullWidth
            type="number"
            label={t("wizard.common.floorRate")}
            value={values.floor_rate ?? ""}
            onChange={(e) => setFieldValue("floor_rate", e.target.value === "" ? null : parseFloat(e.target.value))}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
          />
          <TextField
            fullWidth
            type="number"
            label={t("wizard.common.ceilingRate")}
            value={values.ceiling_rate ?? ""}
            onChange={(e) => setFieldValue("ceiling_rate", e.target.value === "" ? null : parseFloat(e.target.value))}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
          />
        </Stack>
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && hasPIK && revealedFields.has("interest_frequency")}
        label={t("wizard.interestTerms.pikPrincipalQuestion")}
      >
        <OptionCard
          value="yes"
          selected={!!values.interest_on_pik_principal}
          onSelect={() => setFieldValue("interest_on_pik_principal", true)}
          title={t("wizard.interestTerms.pikPrincipalYes")}
          subtitle={t("wizard.interestTerms.pikPrincipalYesDesc")}
        />
        <OptionCard
          value="no"
          selected={!values.interest_on_pik_principal}
          onSelect={() => setFieldValue("interest_on_pik_principal", false)}
          title={t("wizard.interestTerms.pikPrincipalNo")}
          subtitle={t("wizard.interestTerms.pikPrincipalNoDesc")}
        />
      </QuestionSlot>

      {isSimpleLoan && (
        <QuestionSlot
          visible={hasRegularInterest && values.float_type === "linked"}
          label={t("wizard.interestTerms.refDateQuestion")}
        >
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("wizard.interestTerms.refDateLabel")}
            value={values.interest_reference_day ? dayjs(values.interest_reference_day) : null}
            onChange={(v) => setFieldValue("interest_reference_day", v?.isValid() ? toIsoMidday(v) : null)}
            sx={{ width: "100%" }}
          />
        </QuestionSlot>
      )}
    </Box>
  );
};
